export class GeoUtils {
  /**
   * Calculates the destination point given a starting point, distance, and bearing.
   * @param lat Starting latitude in degrees
   * @param lng Starting longitude in degrees
   * @param distance Distance in meters
   * @param bearing Bearing in degrees (0 is North, 90 is East)
   * @returns Object containing lat and lng of the destination point
   */
  static destinationPoint(lat: number, lng: number, distance: number, bearing: number): { lat: number, lng: number } {
    const R = 6371e3; // Earth's radius in meters
    const radLat = this.toRadians(lat);
    const radLng = this.toRadians(lng);
    const radBearing = this.toRadians(bearing);
    const angularDistance = distance / R;

    const destLat = Math.asin(Math.sin(radLat) * Math.cos(angularDistance) +
      Math.cos(radLat) * Math.sin(angularDistance) * Math.cos(radBearing));

    const destLng = radLng + Math.atan2(Math.sin(radBearing) * Math.sin(angularDistance) * Math.cos(radLat),
      Math.cos(angularDistance) - Math.sin(radLat) * Math.sin(destLat));

    return {
      lat: this.toDegrees(destLat),
      lng: this.toDegrees(destLng)
    };
  }

  /**
   * Calculates the coordinates for a sector polygon.
   * @param center object containing lat and lng
   * @param radius Radius in meters
   * @param azimuth Direction the sector points to in degrees
   * @param fov Field of view angle in degrees
   * @returns Array of arrays [lng, lat] for GeoJSON polygon
   */
  static calculateSectorPolygon(center: { lat: number, lng: number }, radius: number, azimuth: number, fov: number): number[][] {
    const coordinates: number[][] = [];

    // Start point (center)
    coordinates.push([center.lng, center.lat]);

    const startAngle = azimuth - (fov / 2);
    const endAngle = azimuth + (fov / 2);
    const steps = 30; // Smoother arc

    for (let i = 0; i <= steps; i++) {
      const angle = startAngle + (endAngle - startAngle) * (i / steps);
      const point = this.destinationPoint(center.lat, center.lng, radius, angle);
      coordinates.push([point.lng, point.lat]);
    }

    // Close the polygon by returning to center
    coordinates.push([center.lng, center.lat]);

    return coordinates;
  }

  /**
   * Ray-casting test for a closed ring of [lng, lat] vertices (planar; fine for local map sectors).
   */
  static pointInPolygonLngLat(point: { lng: number; lat: number }, ring: number[][]): boolean {
    if (!ring || ring.length < 3) {
      return false;
    }
    const x = point.lng;
    const y = point.lat;
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const xi = ring[i][0];
      const yi = ring[i][1];
      const xj = ring[j][0];
      const yj = ring[j][1];
      if (yj === yi) {
        continue;
      }
      const intersect =
        (yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
      if (intersect) {
        inside = !inside;
      }
    }
    return inside;
  }

  static getDistance(p1: { lat: number, lng: number }, p2: { lat: number, lng: number }): number {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = p1.lat * Math.PI / 180;
    const φ2 = p2.lat * Math.PI / 180;
    const Δφ = (p2.lat - p1.lat) * Math.PI / 180;
    const Δλ = (p2.lng - p1.lng) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) *
      Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }

  static getBearing(p1: { lat: number, lng: number }, p2: { lat: number, lng: number }): number {
    const φ1 = this.toRadians(p1.lat);
    const φ2 = this.toRadians(p2.lat);
    const Δλ = this.toRadians(p2.lng - p1.lng);

    const y = Math.sin(Δλ) * Math.cos(φ2);
    const x = Math.cos(φ1) * Math.sin(φ2) -
      Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
    const θ = Math.atan2(y, x);

    return (this.toDegrees(θ) + 360) % 360;
  }

  static toRadians(degrees: number): number {
    return degrees * Math.PI / 180;
  }

  static toDegrees(radians: number): number {
    return radians * 180 / Math.PI;
  }

  /**
   * Calculates the coordinates for a box (rectangle) polygon.
   * @param center object containing lat and lng
   * @param width Width in meters
   * @param height Height in meters
   * @param azimuth Orientation in degrees
   * @returns Array of arrays [lng, lat] for GeoJSON polygon
   */
  static calculateBoxPolygon(center: { lat: number, lng: number }, width: number, height: number, azimuth: number): number[][] {
    const coordinates: number[][] = [];
    const radius = Math.sqrt(Math.pow(width / 2, 2) + Math.pow(height / 2, 2));
    const angleOffset = this.toDegrees(Math.atan2(width, height)); // Angle from the heading axis

    // Top Right
    let point = this.destinationPoint(center.lat, center.lng, radius, azimuth + angleOffset);
    coordinates.push([point.lng, point.lat]);

    // Bottom Right
    point = this.destinationPoint(center.lat, center.lng, radius, azimuth + 180 - angleOffset);
    coordinates.push([point.lng, point.lat]);

    // Bottom Left
    point = this.destinationPoint(center.lat, center.lng, radius, azimuth + 180 + angleOffset);
    coordinates.push([point.lng, point.lat]);

    // Top Left
    point = this.destinationPoint(center.lat, center.lng, radius, azimuth - angleOffset);
    coordinates.push([point.lng, point.lat]);

    // Close polygon
    point = this.destinationPoint(center.lat, center.lng, radius, azimuth + angleOffset);
    coordinates.push([point.lng, point.lat]);

    return coordinates;
  }
}
